import Counter, { type CounterChangeEventHandler } from 'LuxKit/Counter'
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import Sheet from 'components/utils/Sheet'
import { maxFlightPassengerCount } from 'constants/flight'
import { rem } from 'polished'
import { useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

interface Props {
  className?: string
  occupants: App.Occupants
  onChange: (occupants: App.Occupants) => void
  showTitle?: boolean
}

type OccupantType = 'adults' | 'children' | 'infants'

const TravellerCounter = styled(Counter)`
  height: ${rem(40)};
  width: ${rem(108)};
`

function FlightsSearchTravellerPicker(props: Props) {
  const { className, occupants, onChange, showTitle = true } = props
  const intl = useIntl()

  const totalOccupants = useMemo(
    () => occupants.adults + (occupants.children || 0) + (occupants.infants || 0),
    [occupants],
  )

  const handleChange = useCallback((occupantType: OccupantType): CounterChangeEventHandler => (value) => {
    const nextOccupants = { ...occupants, [occupantType]: value }

    onChange({
      ...nextOccupants,
      infants: occupantType === 'adults' ? Math.min(nextOccupants.infants || 0, value) : nextOccupants.infants,
    })
  }, [occupants, onChange])

  const travellerOptions = useMemo(() => [
    {
      id: 'adults' as const,
      label: intl.formatMessage({
        id: 'wOTE7gG6ByomPYOjzWzI',
        defaultMessage: 'Adults (12+)',
        description: 'Traveller type label for adults in the redesigned flights search traveller picker',
      }),
      min: 1,
      max: maxFlightPassengerCount - totalOccupants + occupants.adults,
    },
    {
      id: 'children' as const,
      label: intl.formatMessage({
        id: 'C2IdQhyhEMztx56fDqBk',
        defaultMessage: 'Child (2–11)',
        description: 'Traveller type label for children in the redesigned flights search traveller picker',
      }),
      min: 0,
      max: maxFlightPassengerCount - totalOccupants + (occupants.children || 0),
    },
    {
      id: 'infants' as const,
      label: intl.formatMessage({
        id: 'q50PeIBEI5E2FiHTELqq',
        defaultMessage: 'Infants on lap (under 2)',
        description: 'Traveller type label for lap infants in the redesigned flights search traveller picker',
      }),
      min: 0,
      max: Math.min(occupants.adults, maxFlightPassengerCount - totalOccupants + (occupants.infants || 0)),
    },
  ], [intl, occupants.adults, occupants.children, occupants.infants, totalOccupants])

  return <Sheet className={className} padding={20}>
    <Group direction="vertical" gap={16}>
      {showTitle && <BodyText variant="medium" weight="bold">
        {intl.formatMessage({
          id: 'RvSd9tlg6mZFzzNhOTT3',
          defaultMessage: 'Travellers',
          description: 'Section heading for passenger counts in the redesigned flights search traveller picker',
        })}
      </BodyText>}

      <Group direction="vertical" gap={20}>
        {travellerOptions.map(option => <Group key={option.id} direction="horizontal" horizontalAlign="space-between" verticalAlign="center">

          <BodyText variant="medium">{option.label}</BodyText>

          <TravellerCounter
            data-testid={`flights-search-${option.id}-counter`}
            size="small"
            min={option.min}
            max={option.max}
            value={occupants[option.id] || 0}
            onChange={handleChange(option.id)}
          />
        </Group>)}
      </Group>
    </Group>
  </Sheet>
}

export default FlightsSearchTravellerPicker
